import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useUpdateAccountMutation } from "@/redux/api/accounts.rtkq";
import { profileCompletionSchema, type ProfileCompletionFormValues } from "@/lib/forms/profileCompletionSchema";
import { Building2, Phone, Hash, MapPin } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface ProfileFormProps {
  accountId?: string; // Optional accountId for super admin usage
  initialValues?: Partial<ProfileCompletionFormValues>;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  submitButtonText?: string;
}

const DEFAULT_VALUES: ProfileCompletionFormValues = {
  phoneNumber: "",
  businessName: "",
  businessNumber: "",
  city: "",
};

const valuesKey = (values?: Partial<ProfileCompletionFormValues>) =>
  JSON.stringify({
    phoneNumber: values?.phoneNumber ?? "",
    businessName: values?.businessName ?? "",
    businessNumber: values?.businessNumber ?? "",
    city: values?.city ?? "",
  });

export const ProfileForm: React.FC<ProfileFormProps> = ({
  accountId: propAccountId,
  initialValues,
  onSuccess,
  onError,
  submitButtonText = "שמור שינויים",
}) => {
  const auth = useAuth();

  // Use prop accountId if provided (for super admin usage), otherwise use auth account
  const accountId = propAccountId ?? auth.getAccountId();
  const [updateAccount, { isLoading }] = useUpdateAccountMutation();
  const lastResetKey = useRef<string | null>(null);

  const form = useForm<ProfileCompletionFormValues>({
    resolver: zodResolver(profileCompletionSchema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...initialValues,
    },
    mode: "onBlur",
  });

  // Reset only when server values actually change — not on every parent render.
  useEffect(() => {
    const key = valuesKey(initialValues);
    if (!initialValues || lastResetKey.current === key) return;
    lastResetKey.current = key;
    form.reset({
      ...DEFAULT_VALUES,
      ...initialValues,
    });
  }, [initialValues, form]);

  const onSubmit = async (data: ProfileCompletionFormValues) => {
    try {
      if (!accountId) {
        throw new Error("Account ID not found");
      }

      await updateAccount({
        accountId: accountId,
        updates: {
          accountInformation: {
            phoneNumber: data.phoneNumber,
            businessName: data.businessName,
            businessNumber: data.businessNumber || "",
            city: data.city,
            isProfileComplete: true,
          },
        },
      }).unwrap();
      onSuccess?.();
    } catch (error) {
      console.error("Error updating profile:", error);
      onError?.(error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" dir="rtl">
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex w-full items-center justify-start gap-2 text-start">
                <Phone className="h-4 w-4 shrink-0" />
                <span>מספר טלפון</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="050-1234567"
                  {...field}
                  className="text-start"
                />
              </FormControl>
              <FormMessage className="text-start" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="businessName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex w-full items-center justify-start gap-2 text-start">
                <Building2 className="h-4 w-4 shrink-0" />
                <span>שם העסק</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="שם החברה"
                  {...field}
                  className="text-start"
                />
              </FormControl>
              <FormMessage className="text-start" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="businessNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex w-full items-center justify-start gap-2 text-start">
                <Hash className="h-4 w-4 shrink-0" />
                <span>
                  מספר עסק{" "}
                  <span className="font-normal text-muted-foreground">(אופציונלי)</span>
                </span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="123456789"
                  {...field}
                  className="text-start"
                />
              </FormControl>
              <FormMessage className="text-start" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex w-full items-center justify-start gap-2 text-start">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>עיר</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="תל אביב" {...field} className="text-start" />
              </FormControl>
              <FormMessage className="text-start" />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={isLoading}
          className="mt-6 w-full bg-chen-900 py-6 text-lg font-semibold text-white hover:bg-chen-800"
        >
          {isLoading ? "שומר..." : submitButtonText}
        </Button>
      </form>
    </Form>
  );
};

export default ProfileForm;
